import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  Plus,
  Mail,
  MoreVertical,
  Edit,
  Trash2,
  Shield,
  UserPlus,
  Clock,
  CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import DashboardLayout from '../components/DashboardLayout';
import { api } from '../context/AuthContext';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const ROLES = [
  { id: 'owner', name: 'Owner', description: 'Full access to everything', color: 'bg-purple-100 text-purple-700' },
  { id: 'manager', name: 'Manager', description: 'Can manage content and team', color: 'bg-blue-100 text-blue-700' },
  { id: 'editor', name: 'Editor', description: 'Can create and edit content', color: 'bg-emerald-100 text-emerald-700' },
  { id: 'client', name: 'Client', description: 'Can view and approve content', color: 'bg-amber-100 text-amber-700' },
  { id: 'viewer', name: 'Viewer', description: 'View only access', color: 'bg-slate-100 text-slate-700' }
];

const TeamPage = () => {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);

  const [inviteForm, setInviteForm] = useState({
    email: '',
    role: 'editor',
    calendar_ids: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [membersRes, calendarsRes] = await Promise.all([
        api.get('/teams/members'),
        api.get('/calendars')
      ]);
      setMembers(membersRes.data);
      setCalendars(calendarsRes.data);
    } catch (error) {
      console.error('Error fetching team data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteForm.email) {
      toast.error('Please enter an email address');
      return;
    }

    try {
      const response = await api.post('/teams/invite', inviteForm);
      setMembers([...members, response.data]);
      setShowInviteDialog(false);
      setInviteForm({ email: '', role: 'editor', calendar_ids: [] });
      toast.success('Invitation sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    }
  };

  const handleRemoveMember = async () => {
    if (!selectedMember) return;
    try {
      await api.delete(`/teams/members/${selectedMember.member_id}`);
      setMembers(members.filter(m => m.member_id !== selectedMember.member_id));
      toast.success('Member removed');
    } catch (error) {
      toast.error('Failed to remove member');
    } finally {
      setShowDeleteDialog(false);
      setSelectedMember(null);
    }
  };

  const getRoleStyle = (role) => {
    return ROLES.find(r => r.id === role)?.color || 'bg-slate-100 text-slate-700';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="team-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
              <Users className="h-6 w-6 text-emerald-500" />
              Team Management
            </h1>
            <p className="text-slate-600 mt-1">Manage your team members and their permissions</p>
          </div>
          <Button
            onClick={() => setShowInviteDialog(true)}
            className="bg-emerald-500 hover:bg-emerald-600 text-white"
            data-testid="invite-member-btn"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Invite Member
          </Button>
        </div>

        {/* Current User Card */}
        <Card className="border-slate-100 mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <Avatar className="h-12 w-12">
                <AvatarFallback className="bg-emerald-100 text-emerald-700 text-lg">
                  {user?.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-slate-900">{user?.name}</p>
                  <Badge className="bg-purple-100 text-purple-700">Owner</Badge>
                  <Badge variant="outline" className="text-emerald-600 border-emerald-200">You</Badge>
                </div>
                <p className="text-sm text-slate-500">{user?.email}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Team Members */}
        <Card className="border-slate-100">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold">Team Members</CardTitle>
            <Badge variant="secondary">{members.length} member{members.length !== 1 ? 's' : ''}</Badge>
          </CardHeader>
          <CardContent>
            {members.length === 0 ? (
              <div className="text-center py-12">
                <Users className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">No team members yet</h3>
                <p className="text-slate-500 mb-4">Invite team members to collaborate on your content</p>
                <Button
                  onClick={() => setShowInviteDialog(true)}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Invite Your First Member
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {members.map((member, i) => (
                  <motion.div
                    key={member.member_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className="flex items-center justify-between p-4 rounded-lg border border-slate-100 hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <Avatar className="h-10 w-10">
                        <AvatarFallback className="bg-slate-100 text-slate-600">
                          {member.name?.charAt(0) || member.email?.charAt(0) || '?'}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-900">{member.name || member.email}</p>
                          <Badge className={getRoleStyle(member.role)}>
                            {member.role}
                          </Badge>
                          {member.status === 'pending' && (
                            <Badge variant="outline" className="text-amber-600 border-amber-200">
                              <Clock className="h-3 w-3 mr-1" />
                              Pending
                            </Badge>
                          )}
                          {member.status === 'active' && (
                            <Badge variant="outline" className="text-emerald-600 border-emerald-200">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Active
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">{member.email}</p>
                        {member.calendar_access?.length > 0 && (
                          <p className="text-xs text-slate-400 mt-1">
                            Access to {member.calendar_access.length} calendar{member.calendar_access.length !== 1 ? 's' : ''}
                          </p>
                        )}
                      </div>
                    </div>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem>
                          <Edit className="h-4 w-4 mr-2" />
                          Edit Permissions
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => {
                            setSelectedMember(member);
                            setShowDeleteDialog(true);
                          }}
                          className="text-red-600 focus:text-red-600"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Remove
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Roles Overview */}
        <Card className="border-slate-100 mt-6">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Shield className="h-5 w-5 text-slate-400" />
              Role Permissions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {ROLES.map((role) => (
                <div key={role.id} className="p-4 rounded-lg border border-slate-100">
                  <Badge className={role.color}>{role.name}</Badge>
                  <p className="text-sm text-slate-500 mt-2">{role.description}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Invite Dialog */}
        <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Invite Team Member</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type="email"
                    placeholder="colleague@company.com"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                    className="pl-9"
                    data-testid="invite-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Role</Label>
                <Select
                  value={inviteForm.role}
                  onValueChange={(value) => setInviteForm({ ...inviteForm, role: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.filter(r => r.id !== 'owner').map((role) => (
                      <SelectItem key={role.id} value={role.id}>
                        <div className="flex items-center gap-2">
                          <Badge className={role.color} variant="secondary">{role.name}</Badge>
                          <span className="text-sm text-slate-500">{role.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Calendar Access</Label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {calendars.map((cal) => (
                    <label
                      key={cal.calendar_id}
                      className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={inviteForm.calendar_ids.includes(cal.calendar_id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setInviteForm({
                              ...inviteForm,
                              calendar_ids: [...inviteForm.calendar_ids, cal.calendar_id]
                            });
                          } else {
                            setInviteForm({
                              ...inviteForm,
                              calendar_ids: inviteForm.calendar_ids.filter(id => id !== cal.calendar_id)
                            });
                          }
                        }}
                        className="rounded border-slate-300 text-emerald-500 focus:ring-emerald-500"
                      />
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: cal.brand_color }}
                      />
                      <span className="text-sm text-slate-700">{cal.name}</span>
                    </label>
                  ))}
                </div>
                {calendars.length === 0 && (
                  <p className="text-sm text-slate-500">No calendars created yet</p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowInviteDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleInvite}
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                data-testid="send-invite-btn"
              >
                Send Invitation
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove Team Member</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to remove {selectedMember?.email} from your team? They will lose access to all calendars.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleRemoveMember}
                className="bg-red-500 hover:bg-red-600 text-white"
              >
                Remove
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default TeamPage;
